import { useMemo } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import CssBaseline from "@mui/material/CssBaseline";
import Snackbar from "@mui/material/Snackbar";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { AuthProvider } from "./auth/AuthContext";
import { useAuth } from "./auth/useAuth";
import { AppShell } from "./components/AppShell";
import { LoginPage } from "./pages/LoginPage";
import { RehydrationPage } from "./pages/RehydrationPage";
import { ThemeModeProvider } from "./theme/ThemeModeContext";

function SessionExpiredSnackbar() {
  const { sessionExpiredMessage, dismissSessionExpired } = useAuth();
  return (
    <Snackbar
      open={sessionExpiredMessage !== null}
      autoHideDuration={6000}
      onClose={dismissSessionExpired}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
    >
      <Alert
        severity="warning"
        onClose={dismissSessionExpired}
        variant="filled"
        sx={{ width: "100%" }}
      >
        {sessionExpiredMessage}
      </Alert>
    </Snackbar>
  );
}

function AuthGate() {
  const { isLoading, isAuthenticated, user, logout } = useAuth();

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
        }}
      >
        <CircularProgress color="secondary" aria-label="Validando sessão" />
      </Box>
    );
  }

  if (!isAuthenticated || !user) {
    return (
      <>
        <LoginPage />
        <SessionExpiredSnackbar />
      </>
    );
  }

  return (
    <>
      <AppShell user={user} onLogout={logout}>
        <Routes>
          <Route path="/" element={<Navigate to="/single" replace />} />
          <Route path="/single" element={<RehydrationPage kind="single" />} />
          <Route path="/batch" element={<RehydrationPage kind="batch" />} />
          <Route path="*" element={<Navigate to="/single" replace />} />
        </Routes>
      </AppShell>
      <SessionExpiredSnackbar />
    </>
  );
}

export default function App() {
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            retry: 1,
          },
        },
      }),
    [],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeModeProvider>
        <CssBaseline />
        <BrowserRouter>
          <AuthProvider>
            <AuthGate />
          </AuthProvider>
        </BrowserRouter>
      </ThemeModeProvider>
    </QueryClientProvider>
  );
}
